import { Inject, Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivate, NavigationExtras, Router, RouterStateSnapshot } from '@angular/router'
import { Observable, of } from 'rxjs'
import { catchError, switchMap, take, tap } from 'rxjs/operators'
import { ResultsService } from './results.service'
import { getResultContainerIdFromUrlPath } from './results.utility'
import { HttpErrorResponse } from '@angular/common/http'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { getIdFromSlug } from '../shared/utility-functions/slug.utility'

@Injectable()
export class ResultsGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly resultsService: ResultsService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService
  ) {}

  /**
   * Checks for result.data.event_node within CoreAPI response.
   * If there is one, use new event hub routing that's linked with experience-service.
   * If not, proceed.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const resultId = getIdFromSlug(route.params.slug)
    const containerId = getResultContainerIdFromUrlPath(state.url)
    const result$ = this.resultsService.getResult(resultId)

    return result$.pipe(
      take(1),
      switchMap(result => {
        return result.data?.event_node
          ? this.navigateToEventResults(result.data.event_node.slug_uri, containerId)
          : of(true)
      }),
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status >= 500) {
          this.logger.error('Error getting result to navigate to events page', { error })
        }
        return of(false).pipe(tap(_ => this.router.navigate(['/not-found'])))
      })
    )
  }

  private navigateToEventResults(slugUri: string, containerId: number | null): Observable<boolean> {
    const extras: NavigationExtras = !!containerId ? { queryParams: { filter: containerId } } : {}

    return of(false).pipe(tap(() => this.router.navigate([`${slugUri}/results`], extras)))
  }
}
